import { createApp, ref, onMounted, provide, computed, watch } from 'vue';
import { pinia } from '../../store/main.js'
import { useShopStore } from '../../store/shop.js'
import { debugLogger } from '../../helpers/debugLogger.js';
import { errorLogger } from '../../helpers/errorLogger.js';
import { pushDataLayer, productDataFromJsonApi } from '../../helpers/pushDataLayer.js';
import { plugin, defaultConfig } from '@formkit/vue'
import formkitconfig from '../../formKit/formkit.config.js'

import Donation from './components/donation.vue'
import { jsonapiApi } from '../../api/jsonapi.js';

const forms = document.querySelectorAll('.wwf-donation-form')

// Loop through each block to create multiple instance on the page.
forms.forEach(element => {
  // Create a vue instance.
  createApp({
    props: {
      instanceId: String
    },
    setup (props) {
      const shop = useShopStore()
      const product = ref(null); // Initialize node as a reactive reference
      const globalInformation = computed(() => shop.globalInformation)
      const donationFormData = ref({
        single: null,
        singleCustom: null,
        monthly: null,
        monthlyCustom: null,
        paymentTypes: [],
        productType: null,
        paymentType: null,
        prices_single: [],
        prices_monthly: [],
        gift: null,
        giftable: false,
        singleMinimal: null,
        monthlyMinimal: null,
        singleCertificateMinimal: null,
        monthlyCertificateMinimal: null,
        dedication: false,
        submitLabel: 'Przekaż dotację' // default submit label
      });
      const blockAddToCartButton = ref(false)

      // get all data form drupal block settings as props
      props = { ...props, ...drupalSettings.pdb.configuration[props.instanceId]}

      const submitButtonText = computed(() => {
       if (product.value.field_cta_label) {
         return product.value.field_cta_label
       }
       return 'Przekaż dotację'
      })

      const productBundle = ref(props.product_bundle)
      const productUUID = ref(props.product_uuid)

      const addProductToCart = () => {
        const gift = donationFormData.value.gift === true ? 1 : 0
        const price = Number(currentPrice.value)
        const paymentType = selectedPaymentType.value

        if (price && paymentType) {
          blockAddToCartButton.value = true;

          const productData = productDataFromJsonApi(product.value)

          productData.price = price
          productData.prod_add_placement = 'Karta produktu'
          if (paymentType === 'monthly') {
            productData.item_category2 = 'Miesięcznie'
            productData.item_id = product.value.field_sf_action_reg
          } else if (paymentType === 'single') {
            productData.item_category2 = 'Jednorazowo'
            productData.item_id = product.value.field_sf_action_once
          }
          productData.item_category3 = gift === 1 ? 'Na prezent' : 'Dla mnie'

          pushDataLayer('add_to_cart', {
            currency: 'PLN',
            value: price,
            add_placement: 'Karta produktu',
            items: [productData]
          })

          shop.addProductToCart({
            type: 'commerce_product--' + productBundle.value,
            id: productUUID.value,
            meta: {
              field_gift: gift,
              field_payment_type: paymentType,
              quantity: 1,
              combine: false,
              price: price
            }
          }, true, blockAddToCartButton)
          shop.updateCurrentStage('Cart')
        }
      }

      const prepareFormDataObject = (product) => {
        const data = {}
        data.productType = product.type
        data.paymentType = product.field_payment_type_default
        data.paymentTypes = product.field_payment_type
        data.monthly = Number(product.field_price_recurring_merged['0'].number)
        data.single = Number(product.field_price_merged['0'].number)
        data.monthlyCustom = null
        data.singleCustom = null
        data.singleMinimal = product.field_minimal_price_merged ?? null
        data.monthlyMinimal = product.field_minimal_recurring_price_merged ?? null
        data.singleCertificateMinimal = product.field_minimal_price_certificate_merged ?? null
        data.monthlyCertificateMinimal = product.field_minimal_recurring_price_ce_merged ?? null
        data.giftable = product.field_gift_enabled ?? null
        data.gift = product.field_gift_enabled_default
        data.dedication = product.field_dedication_enabled
        data.submitLabel = submitButtonText
        data.prices_single = product.field_price_merged.map(item => {
          return {
            value: Number(item.number),
            label: Number(item.number) + ' ZŁ',
          }
        })
        data.prices_monthly = product.field_price_recurring_merged.map(item => {
          return {
            value: Number(item.number),
            label: Number(item.number) + ' ZŁ',
          }
        })
        data.currentPrice = currentPrice

        return data;
      }

      // Compute payment type based on form state and product data
      const selectedPaymentType = computed(() => {
        if (donationFormData.value.paymentTypes.length !== 1) {
          return donationFormData.value.paymentType
        } else {
          return donationFormData.value.paymentTypes[0]
        }
      })

      // Compute current price based on form state and product data
      const currentPrice = computed(() => {
        let selectedPaymentType = ''

        if (donationFormData.value.paymentTypes.length !== 1) {
          selectedPaymentType = donationFormData.value.paymentType
        } else {
          selectedPaymentType = donationFormData.value.paymentTypes[0]
        }

        if (selectedPaymentType === 'single') {
          if (donationFormData.value.singleCustom) {
            return donationFormData.value.singleCustom
          } else {
            return donationFormData.value.single
          }
        } else {
          if (donationFormData.value.monthlyCustom) {
            return donationFormData.value.monthlyCustom
          } else {
            return donationFormData.value.monthly
          }
        }
      })

      const minCertificatePrice = computed(() => {
        switch (donationFormData.value.paymentType) {
          case 'single':
            return Number(donationFormData.value.singleCertificateMinimal.number)
          case 'monthly':
            return Number(donationFormData.value.monthlyCertificateMinimal.number)
          default:
            return null;
        }
      });

      // Provide dynamic validation string
      const validationSingleMinimal = computed(() => {
        const min = Number(donationFormData.value.singleMinimal.number)
        let max = 10000

        if (donationFormData.value.productType === 'commerce_product--philanthropist') {
          max = 100000
        }

        return `number|(500)max:${max}|(500)min:${min}|require_one:single`;
      });

      // Provide dynamic validation string
      const validationMonthlyMinimal = computed(() => {
        const min = Number(donationFormData.value.monthlyMinimal.number)
        let max = 10000

        if (donationFormData.value.productType === 'commerce_product--philanthropist') {
          max = 100000
        }

        return `number|(500)max:${max}|(500)min:${min}|require_one:monthly`;
      });

      // Provide validation messages
      const validationMessages = computed(() => {
        let min = 0
        let max = 10000

        if (donationFormData.value.productType === 'commerce_product--philanthropist') {
          max = 100000
        }

        if (donationFormData.value.paymentType === 'single') {
          min = Number(donationFormData.value.singleMinimal.number)
        } else {
          min = Number(donationFormData.value.monthlyMinimal.number)
        }

        return {
          number: 'Wpisana kwota nie jest poprawna',
          require_one: 'Wybierz lub podaj własną kwotę wsparcia',
          min: `Minimalna kwota wsparcia to: ${min} ZŁ`,
          max: `Maksymalna kwota wsparcia to: ${max} ZŁ`,
        }
      })

      // Asynchronously fetch node data and reactively update it
      onMounted(async () => {
        shop.fetchGlobalInformation()

        if (productBundle.value && productUUID.value) {
          try {
            const fetchedProductData = await jsonapiApi.getProduct(productUUID.value, 'commerce_product--' + productBundle.value);

            product.value = fetchedProductData // Update the reactive reference
            debugLogger('Product data loaded:', fetchedProductData)

            donationFormData.value = prepareFormDataObject(product.value)
            debugLogger('Form data:', donationFormData)
            setupWatchers()

            const productData = productDataFromJsonApi(product.value)

            pushDataLayer('view_item', {
              currency: 'PLN',
              value: productData.price,
              items: [productData]
            })
          } catch (error) {
            errorLogger('Error fetching product data:', error);
          }
        } else {
          errorLogger('Couldn\'t get product data from paragraph');
        }
      });

      function setupWatchers() {
        watch(() => donationFormData.value?.single, (newValue, oldValue) => {
          if (newValue !== null) {
            if (donationFormData.value.singleCustom !== null) {
              donationFormData.value.singleCustom = null;
            }
          }
        });

        // Watcher for 'singleCustom' field
        watch(() => donationFormData.value?.singleCustom, (newValue, oldValue) => {
          if (newValue !== null) {
            if (donationFormData.value.single !== null) {
              donationFormData.value.single = null;
            }
          }
        });

        // Watcher for 'monthly' field
        watch(() => donationFormData.value?.monthly, (newValue, oldValue) => {
          if (newValue !== null) {
            if (donationFormData.value.monthlyCustom !== null) {
              donationFormData.value.monthlyCustom = null;
            }
          }
        });

        // Watcher for 'monthlyCustom' field
        watch(() => donationFormData.value?.monthlyCustom, (newValue, oldValue) => {
          if (newValue !== null) {
            if (donationFormData.value.monthly !== null) {
              donationFormData.value.monthly = null;
            }
          }
        });

        // Watcher for 'paymentType' field
        watch(() => donationFormData.value?.paymentType, (newValue, oldValue) => {
          if (newValue !== null) {
            if (currentPrice.value < minCertificatePrice.value) {
              donationFormData.value.gift = false
            }
          }
        });
      }

      provide('donationFormData', donationFormData)
      provide('minCertificatePrice', minCertificatePrice)
      provide('currentPrice', currentPrice)
      provide('productUUID', productUUID)
      provide('productBundle', productBundle)
      provide('addProductToCart', addProductToCart)
      provide('validationSingleMinimal', validationSingleMinimal)
      provide('validationMonthlyMinimal', validationMonthlyMinimal)
      provide('validationMessages', validationMessages)
      provide('globalInformation', globalInformation)
      provide('blockAddToCartButton', blockAddToCartButton)
    }
  },{
    instanceId: element.getAttribute('id'),
  }).component ('Donation', Donation)
    .use(pinia)
    .use(plugin, defaultConfig(formkitconfig))
    .mount(element)
})

